<template>
  <div class="reservations-form">
    <form @submit.prevent="submitForm">
      <!-- Experience Selection -->
      <div class="row mb-4">
        <div class="col-md-6">
          <label class="form-label">{{ $t('reservations.experience') }}</label>
          <select 
            class="form-control" 
            v-model="reservation.experienceId" 
            required
            @change="onExperienceChange"
          >
            <option value="">{{ $t('selectExperience') }}</option>
            <option v-for="experience in experiences" :key="experience.id" :value="experience.id">
              {{ experience.name }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ $t('reservations.date') }}</label>
          <input 
            type="date" 
            class="form-control" 
            v-model="reservation.reservationDate" 
            required
            @change="onDateChange"
          />
        </div>
      </div>

      <!-- Experience Availability -->
      <div class="row mb-4">
        <div class="col-12">
          <label class="form-label">{{ $t('timeSlot') }}</label>
          <select 
            class="form-control" 
            v-model="reservation.experienceAvailabilityId" 
            :disabled="!reservation.experienceId || !reservation.reservationDate || availabilities.length === 0"
            required
          >
            <option value="">{{ $t('selectTimeSlot') }}</option>
            <option v-for="availability in availabilities" :key="availability.id" :value="availability.id">
              {{ availability.startTime }} ({{ availability.minPeople }}-{{ availability.maxPeople }} {{ $t('reservations.people') }})
            </option>
          </select>
          <small v-if="!reservation.experienceId || !reservation.reservationDate" class="text-muted">
            {{ $t('selectExperienceAndDate') || 'Selecione uma experiência e data primeiro.' }}
          </small>
          <small v-else-if="availabilities.length === 0" class="text-muted">
            {{ $t('noAvailability') || 'Sem horários disponíveis para esta data.' }}
          </small>
        </div>
      </div>

      <!-- Tourist Information -->
      <div class="row mb-4">
        <div class="col-12">
          <h5>{{ $t('touristInfo') }}</h5>
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ $t('touristName') }}</label>
          <input 
            type="text" 
            class="form-control" 
            v-model="reservation.touristName" 
            required
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ $t('reservations.email') }}</label>
          <input 
            type="email" 
            class="form-control" 
            v-model="reservation.touristEmail" 
            required
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ $t('reservations.phone') }}</label>
          <input 
            type="tel" 
            class="form-control" 
            v-model="reservation.touristPhone" 
            required
          />
        </div>
      </div>

      <!-- Reservation Details -->
      <div class="row mb-4">
        <div class="col-md-4">
          <label class="form-label">{{ $t('numberOfPeople') }}</label>
          <input 
            type="number" 
            class="form-control" 
            v-model="reservation.numberOfPeople" 
            min="1"
            required
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ $t('reservations.language') }}</label>
          <select class="form-control" v-model="reservation.language" required>
            <option value="pt">{{ $t('languages.portuguese') }}</option>
            <option value="en">{{ $t('languages.english') }}</option>
            <option value="es">{{ $t('languages.spanish') }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">{{ $t('reservations.status') }}</label>
          <select class="form-control" v-model="reservation.status" required>
            <option value="Pending">{{ $t('reservations.status.pending') }}</option>
            <option value="Confirmed">{{ $t('reservations.status.confirmed') }}</option>
            <option value="Cancelled">{{ $t('reservations.status.cancelled') }}</option>
          </select>
        </div>
      </div>

      <!-- Payment Information -->
      <div class="row mb-4">
        <div class="col-md-6">
          <label class="form-label">{{ $t('prePaymentAmount') }}</label>
          <input 
            type="number" 
            class="form-control" 
            v-model="reservation.prePaymentAmount" 
            step="0.01"
            min="0"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ $t('paymentMethod') }}</label>
          <select class="form-control" v-model="reservation.paymentMethod">
            <option value="BankTransfer">{{ $t('reservations.paymentMethods.bankTransfer') }}</option>
            <option value="CreditCard">{{ $t('reservations.paymentMethods.creditCard') }}</option>
          </select>
        </div>
      </div>

      <!-- Notes -->
      <div class="row mb-4">
        <div class="col-md-6">
          <label class="form-label">{{ $t('reservations.restrictions') }}</label>
          <textarea 
            class="form-control" 
            rows="3" 
            v-model="reservation.restrictions"
            :placeholder="$t('restrictionsPlaceholder')"
          ></textarea>
        </div>
        <div class="col-md-6">
          <label class="form-label">{{ $t('reservations.notes') }}</label>
          <textarea 
            class="form-control" 
            rows="3" 
            v-model="reservation.notes"
            :placeholder="$t('notesPlaceholder')"
          ></textarea>
        </div>
      </div>

      <!-- Admin Notes -->
      <div class="row mb-4">
        <div class="col-12">
          <label class="form-label">{{ $t('reservations.adminNotes') }}</label>
          <textarea 
            class="form-control" 
            rows="3" 
            v-model="reservation.adminNotes"
            :placeholder="$t('reservations.adminNotesPlaceholder')"
          ></textarea>
        </div>
      </div>

      <!-- People Details -->
      <div class="row mb-4">
        <div class="col-12">
          <h5>{{ $t('reservations.peopleDetails') }}</h5>
          <div v-for="(person, index) in reservation.people" :key="index" class="row mb-2">
            <div class="col-md-6">
              <input 
                type="text" 
                class="form-control" 
                v-model="person.name" 
                :placeholder="$t('reservations.personName')"
                required
              />
            </div>
            <div class="col-md-5">
              <select class="form-control" v-model="person.ageBracket" required>
                <option value="ZeroToFive">{{ $t('reservations.ageBrackets.zerotofive') }}</option>
                <option value="SixToEleven">{{ $t('reservations.ageBrackets.sixtoeleven') }}</option>
                <option value="TwelveToSeventeen">{{ $t('reservations.ageBrackets.twelvetoseventeen') }}</option>
                <option value="Adult">{{ $t('reservations.ageBrackets.adult') }}</option>
                <option value="Senior">{{ $t('reservations.ageBrackets.senior') }}</option>
              </select>
            </div>
            <div class="col-md-1">
              <button 
                type="button" 
                class="btn btn-danger btn-sm" 
                @click="removePerson(index)"
                v-if="reservation.people.length > 1"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <button 
            type="button" 
            class="btn btn-secondary btn-sm" 
            @click="addPerson"
          >
            <i class="fas fa-plus"></i> {{ $t('addPerson') }}
          </button>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="row">
        <div class="col-12">
          <div class="d-flex justify-content-between">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="$emit('cancelled')"
            >
              {{ $t('cancel') }}
            </button>
            <button 
              type="submit" 
              class="btn btn-primary" 
              :disabled="loading || !reservation.experienceAvailabilityId"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
              {{ $t('save') }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { experiencesService } from '@/services/experiences.service.js';

export default {
  name: 'ReservationsForm',
  props: {
    reservation: {
      type: Object,
      default: () => ({
        experienceId: '',
        experienceAvailabilityId: '',
        reservationDate: '',
        touristName: '',
        touristEmail: '',
        touristPhone: '',
        numberOfPeople: 1,
        language: 'pt',
        status: 'Pending',
        prePaymentAmount: 0,
        paymentMethod: 'BankTransfer',
        restrictions: '',
        notes: '',
        adminNotes: '',
        people: [
          {
            name: '',
            ageBracket: 'Adult'
          }
        ]
      })
    }
  },
  data() {
    return {
      loading: false,
      experiences: [],
      availabilities: [],
      touristId: null
    }
  },
  computed: {
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState('reservations', ['createReservationOutput'])
  },
  watch: {
    getExperiencesOutput: {
      handler(newVal) {
        if (newVal && newVal.data && this.experiences.length === 0) {
          this.experiences = newVal.data;
        }
      },
      immediate: true
    }
  },
  async mounted() {
    await this.loadExperiences();
  },
  methods: {
    ...mapActions('experiences', ['getExperiences']),
    ...mapActions('reservations', ['create']),
    
    async loadExperiences() {
      try {
        const input = { pageSize: 1000, pageNumber: 1 };
        const response = await this.getExperiences(input);
        
        // Try different ways to access the data
        let experiencesData = [];
        if (response && response.data) {
          experiencesData = response.data;
        } else if (response && Array.isArray(response)) {
          experiencesData = response;
        } else if (this.getExperiencesOutput && this.getExperiencesOutput.data) {
          experiencesData = this.getExperiencesOutput.data;
        } else if (this.getExperiencesOutput && Array.isArray(this.getExperiencesOutput)) {
          experiencesData = this.getExperiencesOutput;
        } else if (this.experiences && this.experiences.length > 0) {
          experiencesData = this.experiences;
        }
        
        this.experiences = experiencesData;
        
        // If no experiences loaded, try to reload after a short delay
        if (this.experiences.length === 0) {
          setTimeout(() => {
            this.loadExperiences();
          }, 1000);
        }
      } catch (error) {
        console.error('Error loading experiences:', error);
      }
    },
    
    
    async onExperienceChange() {
      this.reservation.experienceAvailabilityId = '';
      this.availabilities = [];
      if (this.reservation.experienceId && this.reservation.reservationDate) {
        await this.loadAvailabilities();
      }
    },
    
    async onDateChange() {
      this.reservation.experienceAvailabilityId = '';
      this.availabilities = [];
      if (this.reservation.experienceId && this.reservation.reservationDate) {
        await this.loadAvailabilities();
      }
    },
    
    async loadAvailabilities() {
      try {
        const input = {
          experienceId: parseInt(this.reservation.experienceId),
          date: this.reservation.reservationDate,
          pageSize: 100,
          pageNumber: 1
        };

        const response = await experiencesService.getAvailabilities(input);

        // Handle different response structures
        let items = [];
        if (response?.data?.data) {
          // PagedResult structure
          items = response.data.data;
        } else if (response?.data) {
          // Direct array
          items = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          items = response;
        }

        this.availabilities = items.map(a => ({
          id: a.id,
          // backend returns TimeSpan like "10:00:00"; show HH:mm
          startTime: (a.startTime || '').toString().slice(0,5),
          minPeople: a.minPeople,
          maxPeople: a.maxPeople
        }));
      } catch (error) {
        console.error('Error loading availabilities:', error);
        this.availabilities = [];
      }
    },
    
    addPerson() {
      this.reservation.people.push({
        name: '',
        ageBracket: 'Adult'
      });
    },
    
    removePerson(index) {
      this.reservation.people.splice(index, 1);
    },
    
    async submitForm() {
      try {
        this.loading = true;
        
        // Create the reservation directly (API will create/update tourist automatically)
        const reservationData = {
          experienceId: parseInt(this.reservation.experienceId),
          experienceAvailabilityId: parseInt(this.reservation.experienceAvailabilityId),
          touristName: this.reservation.touristName,
          touristEmail: this.reservation.touristEmail,
          touristPhone: this.reservation.touristPhone,
          reservationDate: this.reservation.reservationDate,
          numberOfPeople: parseInt(this.reservation.numberOfPeople),
          language: this.reservation.language,
          restrictions: this.reservation.restrictions,
          notes: this.reservation.notes,
          needsPrePayment: this.reservation.prePaymentAmount > 0,
          prePaymentAmount: parseFloat(this.reservation.prePaymentAmount) || 0,
          paymentMethod: this.reservation.paymentMethod,
          status: this.reservation.status,
          adminNotes: this.reservation.adminNotes,
          discountAmount: 0,
          people: this.reservation.people.map(p => ({
            name: p.name,
            ageBracket: p.ageBracket
          }))
        };
        
        await this.create(reservationData);
        
        this.$emit('form-submitted');
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('createdSuccessfully')
        });
        
      } catch (error) {
        console.error('Error creating reservation:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('createError')
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.reservations-form {
  padding: 20px;
}

.form-label {
  font-weight: bold;
  margin-bottom: 5px;
}

.btn {
  margin-right: 10px;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
